import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2,
  ShieldCheck,
  LogOut,
  Check,
  X,
  Trash2,
  Search,
  Clock,
  Users,
  Ban,
  RotateCcw,
  Package,
  Plus,
  Pencil,
  Eye,
  EyeOff,
  ImagePlus,
  Truck,
} from "lucide-react";
import { ALL_LANGUAGES } from "@/i18n/locales";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "clan-admin-token";

const fmtEUR = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(n || 0);

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function langLabel(code) {
  return ALL_LANGUAGES.find((l) => l.code === code)?.native || code || "—";
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

/* ---------------------------------------------------------------- Login */
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-3.5 font-sans text-clan-ink placeholder:text-clan-muted/70 transition-colors duration-300 focus:border-clan-accent focus:bg-clan-ink/[0.06] focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <div className="grid min-h-screen place-items-center bg-clan-bg px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-lg border border-clan-ink/10 bg-clan-surface p-8"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="grid h-12 w-12 place-items-center rounded-sm border border-clan-accent/50 text-clan-accent">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="mt-5 font-serif text-2xl text-clan-ink">Gathering Admin</h1>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-clan-muted">
            The McDonalds · Registrations
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className={field}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={field}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-2 flex items-center justify-center gap-2 rounded-md bg-clan-primary px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------ StatCard */
function StatCard({ icon: Icon, label, value, testid, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-4 rounded-lg border p-5 text-left transition-colors duration-300 ${
        active ? "border-clan-accent/60 bg-clan-accent/10" : "border-clan-ink/10 bg-clan-surface hover:border-clan-accent/40"
      }`}
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-clan-accent/40 text-clan-accent">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-serif text-2xl text-clan-ink">{value}</span>
        <span className="mt-1 font-mono text-[10px] uppercase tracking-widest text-clan-muted">{label}</span>
      </span>
    </button>
  );
}

/* --------------------------------------------------------- StatusBadge */
function StatusBadge({ status }) {
  const map = {
    pending: "border-amber-300 bg-amber-100 text-amber-800",
    confirmed: "border-green-300 bg-green-100 text-green-800",
    refused: "border-red-300 bg-red-100 text-red-800",
  };
  return (
    <span
      className={`inline-block rounded-md border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${map[status] || map.pending}`}
    >
      {status || "pending"}
    </span>
  );
}

/* ----------------------------------------------------- ModerationSection */
// Generic approve/refuse/delete list for visitor-submitted events & recipes.
function ModerationSection({ client, onAuthError, kind }) {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await client.get(`/admin/${kind}${q}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!onAuthError(err)) toast.error(`Could not load ${kind}.`);
    } finally {
      setLoading(false);
    }
  }, [client, filter, kind, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await client.patch(`/admin/${kind}/${id}`, { status });
      toast.success(`${kind === "events" ? "Event" : "Recipe"} ${status === "confirmed" ? "accepted" : status}.`);
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await client.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const tabs = ["pending", "confirmed", "refused", "all"];

  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={`${kind}-filters`}>
        {tabs.map((tb) => (
          <button
            key={tb}
            data-testid={`${kind}-filter-${tb}`}
            onClick={() => setFilter(tb)}
            className={`rounded-md border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              filter === tb ? "border-clan-accent bg-clan-accent/15 text-clan-ink" : "border-clan-ink/12 text-clan-muted hover:border-clan-accent/50"
            }`}
          >
            {tb === "confirmed" ? "accepted" : tb}
          </button>
        ))}
      </div>

      <div className="mt-6" data-testid={`admin-${kind}-list`}>
        {loading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-clan-accent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
            <p className="font-serif text-xl italic text-clan-muted">Nothing here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {rows.map((r) => {
              const label = kind === "events" ? `${r.type} · ${r.city}` : r.title;
              return (
                <div
                  key={r.id}
                  data-testid={`admin-${kind}-${r.id}`}
                  className="flex flex-col gap-4 rounded-lg border border-clan-ink/10 bg-clan-surface p-5 md:flex-row md:items-start md:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="font-serif text-lg text-clan-ink">{label}</p>
                      <StatusBadge status={r.status} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-clan-muted">{fmtDate(r.created_at)}</span>
                    </div>
                    {kind === "events" ? (
                      <>
                        <p className="mt-1 font-mono text-xs text-clan-accent/90">
                          {r.city}, {r.country}
                          {r.event_at ? ` · ${fmtDate(r.event_at)}${r.event_end ? ` – ${fmtDate(r.event_end)}` : ""}` : ""}
                        </p>
                        <p className="mt-2 text-sm text-clan-muted">{r.description}</p>
                        {r.image_url && (
                          <img src={r.image_url} alt="event" className="mt-3 h-28 w-full max-w-xs rounded object-cover" />
                        )}
                      </>
                    ) : (
                      <>
                        <p className="mt-1 font-mono text-xs text-clan-accent/90">
                          {r.submitter}{(r.city || r.country) ? ` · ${[r.city, r.country].filter(Boolean).join(", ")}` : ""}
                        </p>
                        {r.description && <p className="mt-2 text-sm italic text-clan-muted">{r.description}</p>}
                        <p className="mt-2 whitespace-pre-line text-xs text-clan-muted"><b>Ingredients:</b> {r.ingredients}</p>
                        <p className="mt-1 whitespace-pre-line text-xs text-clan-muted"><b>Method:</b> {r.method}</p>
                        {r.image_url && (
                          <img src={r.image_url} alt="dish" className="mt-3 h-28 w-full max-w-xs rounded object-cover" />
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {r.status !== "confirmed" && (
                      <button data-testid={`${kind}-accept-${r.id}`} disabled={busyId === r.id} onClick={() => setStatus(r.id, "confirmed")} className="flex items-center gap-1.5 rounded-md border border-clan-accent/50 bg-clan-accent/10 px-3.5 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-accent transition-colors hover:bg-clan-accent/20 disabled:opacity-50">
                        <Check className="h-3.5 w-3.5" /> Accept
                      </button>
                    )}
                    {r.status !== "refused" && (
                      <button data-testid={`${kind}-refuse-${r.id}`} disabled={busyId === r.id} onClick={() => setStatus(r.id, "refused")} className="flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3.5 py-2 font-mono text-[10px] uppercase tracking-widest text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50">
                        <X className="h-3.5 w-3.5" /> Refuse
                      </button>
                    )}
                    <button data-testid={`${kind}-delete-${r.id}`} disabled={busyId === r.id} onClick={() => remove(r.id, label)} aria-label="Delete" className="flex items-center gap-1.5 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:border-red-400 hover:text-red-700 disabled:opacity-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------- ProductsSection */
const EMPTY_PRODUCT = { name: "", description: "", price: "", category: "", stock: "-1", image_url: "", active: true };

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);

  const handleUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.78);
        if (dataUrl.length > 900000) {
          toast.error("That image is too large — try a smaller one or paste a link.");
          return;
        }
        setForm((f) => ({ ...f, image_url: dataUrl }));
        toast.success("Photo added.");
      };
      img.onerror = () => toast.error("Could not read that image.");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price === "" || Number.isNaN(Number(form.price))) {
      toast.error("A name and a valid price are required.");
      return;
    }
    onSave({
      name: form.name.trim(),
      description: form.description.trim(),
      price: Number(form.price),
      category: form.category.trim() || "General",
      stock: form.stock === "" ? -1 : parseInt(form.stock, 10),
      image_url: form.image_url.trim(),
      active: form.active,
    });
  };

  const field =
    "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-2.5 font-sans text-sm text-clan-ink placeholder:text-clan-muted/70 focus:border-clan-accent focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <form onSubmit={submit} data-testid="product-form" className="rounded-lg border border-clan-accent/30 bg-clan-surface p-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className={`${field} sm:col-span-2`} />
        <input data-testid="product-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price in EUR (e.g. 39.90)" inputMode="decimal" className={field} />
        <input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (e.g. Apparel)" className={field} />
        <input data-testid="product-stock-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock (-1 = unlimited)" inputMode="numeric" className={field} />
        <input data-testid="product-image-url-input" value={form.image_url.startsWith("data:") ? "" : form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image link (optional)" className={field} />
      </div>
      <textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" rows={2} className={`${field} mt-3`} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label data-testid="product-image-upload-label" className="flex cursor-pointer items-center gap-2 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:border-clan-accent/60 hover:text-clan-ink">
          <ImagePlus className="h-3.5 w-3.5" /> Upload photo
          <input data-testid="product-image-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
        </label>
        {form.image_url && <img src={form.image_url} alt="preview" className="h-10 w-10 rounded object-cover" />}
        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-clan-muted">
          <input data-testid="product-active-input" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Visible in store
        </label>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <button data-testid="product-save-btn" type="submit" disabled={saving} className="flex items-center gap-2 rounded-md bg-clan-primary px-6 py-2.5 font-mono text-[10px] uppercase tracking-widest text-white transition-colors hover:bg-clan-primaryHover disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
        </button>
        {onCancel && (
          <button type="button" data-testid="product-cancel-btn" onClick={onCancel} className="rounded-md border border-clan-ink/15 px-6 py-2.5 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:text-clan-ink">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function ProductsSection({ client, onAuthError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/products");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Could not load products.");
    } finally {
      setLoading(false);
    }
  }, [client, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (payload) => {
    setSaving(true);
    try {
      await client.post("/admin/products", payload);
      toast.success("Product added.");
      setCreating(false);
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const update = async (id, payload) => {
    setSaving(true);
    try {
      await client.patch(`/admin/products/${id}`, payload);
      toast.success("Product updated.");
      setEditId(null);
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    setBusyId(p.id);
    try {
      await client.patch(`/admin/products/${p.id}`, { active: !p.active });
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    setBusyId(p.id);
    try {
      await client.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted.");
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-clan-muted">{rows.length} products</p>
        {!creating && (
          <button data-testid="product-new-btn" onClick={() => { setCreating(true); setEditId(null); }} className="flex items-center gap-2 rounded-md border border-clan-accent/50 bg-clan-accent/10 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-accent transition-colors hover:bg-clan-accent/20">
            <Plus className="h-3.5 w-3.5" /> New product
          </button>
        )}
      </div>

      {creating && (
        <div className="mb-6">
          <ProductForm initial={EMPTY_PRODUCT} onSave={create} onCancel={() => setCreating(false)} saving={saving} />
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-clan-accent" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
          <p className="font-serif text-xl italic text-clan-muted">No products yet.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="admin-products-list">
          {rows.map((p) =>
            editId === p.id ? (
              <ProductForm
                key={p.id}
                initial={{ name: p.name, description: p.description || "", price: String(p.price), category: p.category || "", stock: String(p.stock), image_url: p.image_url || "", active: p.active }}
                onSave={(payload) => update(p.id, payload)}
                onCancel={() => setEditId(null)}
                saving={saving}
              />
            ) : (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 rounded-lg border border-clan-ink/10 bg-clan-surface p-4">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="h-16 w-16 shrink-0 rounded-md object-cover" />
                ) : (
                  <span className="grid h-16 w-16 shrink-0 place-items-center rounded-md bg-clan-ink/[0.05] text-clan-muted"><Package className="h-6 w-6" /></span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-serif text-lg text-clan-ink">{p.name}</p>
                    {!p.active && <span className="rounded-md border border-clan-ink/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-clan-muted">hidden</span>}
                  </div>
                  <p className="mt-0.5 font-mono text-xs text-clan-accent">
                    {fmtEUR(p.price)} · {p.category} · {p.stock === -1 ? "∞ stock" : `${p.stock} in stock`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button data-testid={`product-toggle-${p.id}`} disabled={busyId === p.id} onClick={() => toggleActive(p)} aria-label="Toggle visibility" className="grid h-9 w-9 place-items-center rounded-md border border-clan-ink/15 text-clan-muted transition-colors hover:border-clan-accent/60 hover:text-clan-ink disabled:opacity-50">
                    {p.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button data-testid={`product-edit-${p.id}`} onClick={() => { setEditId(p.id); setCreating(false); }} aria-label="Edit" className="grid h-9 w-9 place-items-center rounded-md border border-clan-ink/15 text-clan-muted transition-colors hover:border-clan-accent/60 hover:text-clan-ink">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`product-delete-${p.id}`} disabled={busyId === p.id} onClick={() => remove(p)} aria-label="Delete" className="grid h-9 w-9 place-items-center rounded-md border border-clan-ink/15 text-clan-muted transition-colors hover:border-red-400 hover:text-red-700 disabled:opacity-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------- OrdersSection */
function OrdersSection({ client, onAuthError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [track, setTrack] = useState({}); // { [orderId]: { tracking, carrier } }

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/orders");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, [client, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const ship = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await client.post(`/admin/orders/${o.id}/ship`, {
        tracking: track[o.id]?.tracking || "",
        carrier: track[o.id]?.carrier || "",
      });
      toast.success(data.email_sent ? "Marked shipped — customer emailed." : "Marked shipped (email could not be sent).");
      await load();
    } catch (err) {
      if (!onAuthError(err)) toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-clan-accent" /></div>;
  if (rows.length === 0)
    return (
      <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
        <p className="font-serif text-xl italic text-clan-muted">No paid orders yet.</p>
      </div>
    );

  const inputCls = "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-3 py-2 font-sans text-sm text-clan-ink placeholder:text-clan-muted/70 focus:border-clan-accent focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <div className="flex flex-col gap-3" data-testid="admin-orders-list">
      {rows.map((o) => {
        const ship_ = o.shipping || {};
        const addr = ship_.address || {};
        const shipped = o.fulfillment_status === "shipped";
        return (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-lg border border-clan-ink/10 bg-clan-surface p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-md border border-green-300 bg-green-100 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-green-800">paid</span>
                <span className={`rounded-md border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${shipped ? "border-clan-accent/50 bg-clan-accent/10 text-clan-accent" : "border-amber-300 bg-amber-100 text-amber-800"}`}>
                  {shipped ? "shipped" : "processing"}
                </span>
                <span className="font-serif text-lg text-clan-ink">{fmtEUR(o.amount)}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-clan-muted">{fmtDate(o.created_at)}</span>
              </div>
              {o.customer_email && <span className="font-mono text-xs text-clan-accent">{o.customer_email}</span>}
            </div>
            <ul className="mt-3 flex flex-col gap-1">
              {(o.items || []).map((it, i) => (
                <li key={i} className="flex justify-between text-sm text-clan-ink/80">
                  <span>{it.quantity} × {it.name}</span>
                  <span className="font-mono text-xs text-clan-muted">{fmtEUR(it.price * it.quantity)}</span>
                </li>
              ))}
            </ul>
            {(ship_.name || addr.line1) && (
              <p className="mt-3 border-t border-clan-ink/10 pt-3 text-xs text-clan-muted">
                <b className="text-clan-ink/70">Ship to:</b> {ship_.name} — {[addr.line1, addr.line2, addr.postal_code, addr.city, addr.country].filter(Boolean).join(", ")}
              </p>
            )}

            {shipped ? (
              o.tracking && (
                <p className="mt-3 font-mono text-xs text-clan-accent">
                  Tracking: {o.carrier ? `${o.carrier} · ` : ""}{o.tracking}
                </p>
              )
            ) : (
              <div className="mt-4 flex flex-col gap-2 border-t border-clan-ink/10 pt-4 sm:flex-row sm:items-center">
                <input
                  data-testid={`order-carrier-${o.id}`}
                  value={track[o.id]?.carrier || ""}
                  onChange={(e) => setTrack((t) => ({ ...t, [o.id]: { ...t[o.id], carrier: e.target.value } }))}
                  placeholder="Carrier (optional)"
                  className={`${inputCls} sm:max-w-[180px]`}
                />
                <input
                  data-testid={`order-tracking-${o.id}`}
                  value={track[o.id]?.tracking || ""}
                  onChange={(e) => setTrack((t) => ({ ...t, [o.id]: { ...t[o.id], tracking: e.target.value } }))}
                  placeholder="Tracking number (optional)"
                  className={`${inputCls} sm:max-w-[240px]`}
                />
                <button
                  data-testid={`order-ship-${o.id}`}
                  disabled={busyId === o.id}
                  onClick={() => ship(o)}
                  className="flex items-center justify-center gap-2 rounded-md bg-clan-primary px-5 py-2.5 font-mono text-[10px] uppercase tracking-widest text-white transition-colors hover:bg-clan-primaryHover disabled:opacity-60"
                >
                  {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />}
                  Mark shipped & email
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------- Dashboard */
function AdminDashboard({ token, onLogout }) {
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, refused: 0, total: 0 });
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [section, setSection] = useState("registrations");
  const loggedOutRef = useRef(false);

  const client = useMemo(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        if (!loggedOutRef.current) {
          loggedOutRef.current = true;
          toast.error("Session expired. Please sign in again.");
          onLogout();
        }
        return true;
      }
      return false;
    },
    [onLogout]
  );

  const loadStats = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/stats");
      setStats(data);
    } catch (err) {
      handleAuthError(err);
    }
  }, [client, handleAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await client.get(`/admin/registrations${q}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not load registrations.");
    } finally {
      setLoading(false);
    }
  }, [client, filter, handleAuthError]);

  useEffect(() => {
    load();
    loadStats();
  }, [load, loadStats]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await client.patch(`/admin/registrations/${id}`, { status });
      toast.success(`Registration ${status}.`);
      await Promise.all([load(), loadStats()]);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete the registration for "${name}"? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await client.delete(`/admin/registrations/${id}`);
      toast.success("Registration deleted.");
      await Promise.all([load(), loadStats()]);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-clan-bg">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-clan-ink/10 bg-clan-bg/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-sm border border-clan-accent/50 text-clan-accent">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-serif text-lg text-clan-ink">Gathering Admin</span>
              <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.3em] text-clan-muted">
                The McDonalds
              </span>
            </div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="flex items-center gap-2 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-clan-ink transition-colors hover:border-clan-accent/60 hover:bg-clan-ink/[0.06]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {/* Section switcher */}
        <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-sections">
          {[
            { k: "registrations", label: "Registrations" },
            { k: "events", label: "Events" },
            { k: "recipes", label: "Recipes" },
            { k: "products", label: "Store" },
            { k: "orders", label: "Orders" },
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`admin-section-${s.k}`}
              onClick={() => setSection(s.k)}
              className={`rounded-md border px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                section === s.k ? "border-clan-accent bg-clan-accent/15 text-clan-ink" : "border-clan-ink/12 text-clan-muted hover:border-clan-accent/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {section === "events" && <ModerationSection client={client} onAuthError={handleAuthError} kind="events" />}
        {section === "recipes" && <ModerationSection client={client} onAuthError={handleAuthError} kind="recipes" />}
        {section === "products" && <ProductsSection client={client} onAuthError={handleAuthError} />}
        {section === "orders" && <OrdersSection client={client} onAuthError={handleAuthError} />}

        {section === "registrations" && (
        <>
        {/* Stats / filters */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Clock} label="Pending" value={stats.pending} testid="stat-pending" active={filter === "pending"} onClick={() => setFilter("pending")} />
          <StatCard icon={Check} label="Confirmed" value={stats.confirmed} testid="stat-confirmed" active={filter === "confirmed"} onClick={() => setFilter("confirmed")} />
          <StatCard icon={Ban} label="Refused" value={stats.refused} testid="stat-refused" active={filter === "refused"} onClick={() => setFilter("refused")} />
          <StatCard icon={Users} label="All" value={stats.total} testid="stat-all" active={filter === "all"} onClick={() => setFilter("all")} />
        </div>

        {/* Search */}
        <div className="mt-8 flex items-center gap-3 rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-3">
          <Search className="h-4 w-4 text-clan-muted" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-transparent font-sans text-clan-ink placeholder:text-clan-muted/70 focus:outline-none"
          />
        </div>

        {/* List */}
        <div className="mt-6" data-testid="admin-registrations-list">
          {loading ? (
            <div className="grid place-items-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-clan-accent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
              <p className="font-serif text-xl italic text-clan-muted">No registrations here.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-reg-${r.id}`}
                  className="flex flex-col gap-4 rounded-lg border border-clan-ink/10 bg-clan-surface p-5 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="font-serif text-lg text-clan-ink">{r.name}</p>
                      <StatusBadge status={r.status} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-clan-muted">
                        {langLabel(r.language)} · {fmtDate(r.created_at)}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-xs text-clan-accent/90">{r.email}</p>
                    {r.message && (
                      <p className="mt-2 border-l border-clan-accent/40 pl-3 text-sm italic text-clan-muted">
                        “{r.message}”
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {r.status !== "confirmed" && (
                      <button
                        data-testid={`confirm-btn-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => setStatus(r.id, "confirmed")}
                        title="Confirm"
                        className="flex items-center gap-1.5 rounded-md border border-clan-accent/50 bg-clan-accent/10 px-3.5 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-accent transition-colors hover:bg-clan-accent/20 disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" /> Confirm
                      </button>
                    )}
                    {r.status !== "refused" && (
                      <button
                        data-testid={`refuse-btn-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => setStatus(r.id, "refused")}
                        title="Refuse"
                        className="flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3.5 py-2 font-mono text-[10px] uppercase tracking-widest text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" /> Refuse
                      </button>
                    )}
                    {r.status !== "pending" && (
                      <button
                        data-testid={`pending-btn-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => setStatus(r.id, "pending")}
                        title="Move back to pending"
                        aria-label={`Move ${r.name} back to pending`}
                        className="flex items-center gap-1.5 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:bg-clan-ink/[0.06] disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`delete-btn-${r.id}`}
                      disabled={busyId === r.id}
                      onClick={() => remove(r.id, r.name)}
                      title="Delete"
                      aria-label={`Delete ${r.name}`}
                      className="flex items-center gap-1.5 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:border-red-400 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}

/* ----------------------------------------------------------------- Page */
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      <Toaster position="top-center" theme="dark" />
      {token ? (
        <AdminDashboard token={token} onLogout={logout} />
      ) : (
        <AdminLogin onLogin={setToken} />
      )}
    </>
  );
}
